"use client";

import cn from "@utils/cn";
import { useFormatter, useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { FC } from "react";

import Share from "@/components/ShareComponent/Share";
import AnchorLink from "@/components/ui/AnchorLink/AnchorLink";
import Icon from "@/components/ui/Icon/Icon";
import { ArticleProps } from "@/types";

const ArticleTemplateHero: FC<ArticleProps> = ({ entry }) => {
  const pathname = usePathname();
  const path = pathname?.split("/");
  const t = useTranslations();
  const format = useFormatter();
  let formattedDate = "";
  if (entry.postDate) {
    const dateTime = new Date(entry.postDate);

    formattedDate = dateTime.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }
  let updatedAt = "";
  if (entry?.updatedAt) {
    const dateTime = new Date(entry?.updatedAt);
    updatedAt = dateTime.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }

  const getSectionTitle = (lang: string, section: string) => {
    const mapping: Record<string, Record<string, string>> = {
      en: {
        blog: "BLOG",
        news: "LATEST NEWS",
      },
      ar: {
        blog: "المدونة",
        news: "آخر الأخبار",
      },
    };

    return mapping[lang]?.[section] || section.toUpperCase();
  };

  const getSectionTitleHome = (lang: string, section: string) => {
    const mapping: Record<string, Record<string, string>> = {
      en: {
        blog: "Home",
      },
      ar: {
        blog: "الرئيسية",
      },
    };

    return mapping[lang]?.[section] || section.toUpperCase();
  };

  return (
    <div
      className={cn("p-m md:p-0", entry.image && "relative bg-cover bg-center text-white h-svh md:h-auto md:aspect-[2/1]")}
      style={entry.image && { backgroundImage: `url(${entry.image.url}` }}
    >
      {entry.image && <div className="absolute z-10 w-full h-full img-overlay-article-hero top-0 left-0"></div>}
      <div className={cn("px-s md:px-0 md:w-20\\/24 md:mx-auto pb-[40px]", entry.image ? "pt-3xl md:pt-5xl" : "pt-l")}>
        <div className="flex items-center justify-between z-20 mb-l relative">
          <div className="flex items-center ">
            <AnchorLink href={"../"} key={path?.[2]} disabled={false} className={cn("underline underline-offset-2")}>
              <span className="flex items-center text-s">
                {/* {path?.[2].toUpperCase()} */}
                {/* {getSectionTitle(path[1], path[2])} */}
                {getSectionTitleHome(path[1]?.split("-")[0], path[2])}
                <Icon name="caret-right" size={"20"} className={cn(entry.image ? "stroke-white" : "stroke-black")}></Icon>
              </span>
            </AnchorLink>
            <AnchorLink href={"../"} key={path?.[3]} disabled={false} className={cn("underline underline-offset-2")}>
              <span className="flex items-center text-s">
                {getSectionTitle(path[1]?.split("-")[0], path[2])}
                {/* {entry.articleType[0].toUpperCase() + entry.articleType.substring(1).replace("-", " ")} */}
                <Icon name="caret-right" size={"20"} className={cn(entry.image ? "stroke-white" : "stroke-black")}></Icon>
              </span>
            </AnchorLink>
            <AnchorLink href={""} key={entry.slug} className={cn("text-inherit !bg-transparent")} span={true}>
              <span className="flex items-center text-s">
                {entry.internalName.substring(0, 30)}
                {entry.internalName.length > 30 && "..."}
              </span>
            </AnchorLink>
          </div>
          {typeof window !== "undefined" ? (
            <Share shareData={{ title: entry.internalName, text: entry.internalName, url: window.location.href }}>
              <Icon name="share" size={"20"} className={cn(entry.image ? "stroke-white" : "stroke-black")} />
            </Share>
          ) : (
            <button>
              <Icon name="share" size={"20"} className={cn(entry.image ? "stroke-white" : "stroke-black")} />
            </button>
          )}
        </div>
        <div className={cn(entry.image && "absolute bottom-[40px] md:bottom-[24px] z-10 left-[50%] translate-x-[-50%] w-full md:w-20\\/24")}>
          <div
            className={cn("flex flex-col items-center text-center", !entry.centerContent && !entry.image && "items-start text-center md:text-left")}
          >
            <div className="flex items-center text-s mx-auto md:text-xs mb-xl md:mb-l">
              <span className="after:content-['·'] after:p-xs">{formattedDate != "" && formattedDate}</span>
              {entry.tags?.map((tag, i) => (
                <span className="after:content-['·'] after:p-xs last:after:hidden" key={`tag-${i}`}>
                  {t("article.tag." + tag)}
                </span>
              ))}
            </div>
            <h1 className="font-primary font-medium uppercase text-l md:text-xl">{entry.internalName}</h1>
            {updatedAt && (
              <div className="flex items-center text-s mx-auto md:text-[12px] mt-xl md:mt-l">
                <span className="after:content-['·'] after:p-xs">
                  {t("common.label.lastUpdated")}: {updatedAt != "" && updatedAt}
                </span>
              </div>
            )}
            {entry.secondaryTags && (
              <div className="flex items-center gap-xs mx-auto mt-xl md:mt-l">
                {entry.secondaryTags.map((tag, i) => (
                  <span
                    className="text-s uppercase rounded px-xs py-xxs text-black"
                    key={`bgTag-${i}`}
                    style={{ backgroundColor: tag.color || "#ffffff" }}
                  >
                    {tag.text}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleTemplateHero;
